/*
 * MapLibrary is a facade for accessing a subset of functions of a third-party map provider subsystem.
 * By having a facade, non of the map components will ever need to know how to perform map operations,
 * instead of that they will rely on the facade in order to accomplish that.
 * For example, if we need to draw a boundary, we are going to ask the facade to draw it for us. The
 * facade internally will know how to retrieve a map center point, and how to draw a boundary around it,
 * these 2 map operations don't need to be know by any map component, they are known just by the facade.
 * From the previous example, notice how we achieved the interaction of 2 map operations just by
 * calling the facade and without knowing how the boundary drawing function works internally in order to
 * obtain the desired outcome of having a boundary represented in the map.
 */

// State object for the map library facade.

const mapInstance = {
  isGoogleMapLoaded: false,
  isGoogleMapsLoading: false,
  map: null,
  locationBoundary: null,
  mapId: 'bdxproducts',
  markers: [],
  hoveredMarker: null,
  markersHashed: new Map(),
  sitePlans: [],
  interactiveMarkersOverlays: [],
};

/**
 * map cursosr values supported by Google Maps.
 */
const mapCursor = Object.freeze({
  pointer: 'pointer',
  grab: 'grab',
  grabbing: 'grabbing',
  default: '',
});

// Interval to sample waiting for the load event; this number is arbitrary.
const googleMapsLoadSampleRate = 100;

// Lazy-dev, the facade should be able to load the Google Maps script by itself but to remove the complexity
// of sharing the Google Maps API key with the JavaScript, we are defining the script in an HTML element from the page.
const googleMapsSource = document.querySelector('[google-maps-source]')?.href;

/**
 * If the Google Maps API fails to load, set the loading and loaded flags to false and reject the promise.
 * @param reject - A function that rejects the promise.
 */
const onGoogleMapsError = (reject) => {
  mapInstance.isGoogleMapsLoading = false;
  mapInstance.isGoogleMapLoaded = false;
  reject(new Error(`Unable to load Google Maps API.`));
};

/**
 * When the Google Maps API is loaded, set the isGoogleMapLoaded property of the mapInstance object to
 * true and resolve the promise.
 * @param resolve - A function that is called when the promise is resolved.
 */
const onGoogleMapsLoaded = (resolve) => {
  mapInstance.isGoogleMapLoaded = true;
  resolve();
};

/**
 * Create a script element, set its source to the googlemapssource,
 * set its onerror property to a function that rejects the promise,
 * set its onload property to a function that resolves the promise.
 * @param resolve - The function to call when the script has loaded.
 * @param reject - A function that is called when the promise is rejected.
 */
const appendGoogleMapsScript = (resolve, reject) => {
  const script = document.createElement('script');
  script.src = googleMapsSource;
  script.async = true;
  script.onerror = onGoogleMapsError.bind(this, reject);
  mapInstance.onload = onGoogleMapsLoaded.bind(this, resolve);
  document.head.appendChild(script);
};

window.initMap = () => mapInstance.onload();

/**
 * If the map is loaded, resolve the promise.
 * If not, wait the amount of time from googleMapsLoadSampleRate and try again.
 * @param resolve - The resolve function of the promise.
 */
const isGoogleMapsLoaded = (resolve) => {
  if (mapInstance.isGoogleMapLoaded) {
    resolve();
  } else {
    setTimeout(isGoogleMapsLoaded.bind(null, resolve), googleMapsLoadSampleRate);
  }
};

/**
 * Wait the amount of time from googleMapsLoadSampleRate for Google Maps to load, and then resolve the promise.
 * @returns A promise that will resolve when the google maps api is loaded.
 */
const waitForGoogleMaps = () =>
  new Promise((resolve) => {
    setTimeout(isGoogleMapsLoaded.bind(null, resolve), googleMapsLoadSampleRate);
  });

/**
 * Creates the hashed key to represent markers on the map. Hash key is used to several markers sharing the same
 * location can be allocated under the same key.
 */
export function createMarkerHashedKey(latitude, longitude) {
  return `${(+latitude).toFixed(2)},${(+longitude).toFixed(2)}`;
}

const hashMarker = (marker, advancedMarker) => {
  const key = createMarkerHashedKey(marker.markerPosition.lat, marker.markerPosition.lng);

  if (mapInstance.markersHashed.has(key)) {
    const markersOnThisPosition = mapInstance.markersHashed.get(key);
    const values = [...markersOnThisPosition, advancedMarker];
    mapInstance.markersHashed.set(key, values);
  } else {
    const values = [advancedMarker];
    mapInstance.markersHashed.set(key, values);
  }
};

/**
 * Creates a DOM element from the provided HTML content string.
 *
 * @param {string} content - The HTML content as a string.
 * @returns {Node} The first element child of the created wrapper.
 */
const createMarkerContentElement = (content) => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = content;
  const element = wrapper.firstElementChild;
  return element;
};

/**
 * It creates a new marker if the marker doesn't already exist.
 * @param marker - object with the information to build the marker.
 * @returns A promise that resolves to a new marker.
 */
const createMarker = (marker) => {
  const content = createMarkerContentElement(marker.icon);
  const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
    content,
    position: marker.markerPosition,
    zIndex: marker.zIndex,
    title: marker.description,
    gmpClickable: true,
  });

  advancedMarker.communityId = marker.metaData.communityId;
  advancedMarker.id = marker.metaData.id;
  advancedMarker.ignoreVisibilityUpdates = marker.ignoreVisibilityUpdates;

  // Add the data-marker-element="true" to the container element of the AdvancedMarkerElement,
  // with the purpose getting the main div of the marker.
  advancedMarker.element.dataset.markerElement = 'true';
  advancedMarker.element.setAttribute('aria-label', marker.description);

  advancedMarker.addListener('click', marker.events.click.bind(null, marker));
  mapInstance.markers.push(advancedMarker);
  hashMarker(marker, advancedMarker);
};

/**
 * It hides or show a marker if it's inside the map boundaries.
 * @param marker - an object the contains the information of the market to delete.
 */
const setMarkerVisibility = (marker, bounds, zoom) => {
  if (marker.ignoreVisibilityUpdates) {
    return;
  }

  const isVisible = bounds.contains(marker.position);

  if (zoom < 8) {
    marker.collisionBehavior = google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY;
  } else {
    marker.collisionBehavior = google.maps.CollisionBehavior.REQUIRED;
  }

  if (!isVisible && marker.map) {
    marker.map = null;
  }

  if (isVisible && !marker.map) {
    marker.map = mapInstance.map;
  }
};

/**
 * A function that is called when the map is visible or not.
 */
const onMapVisibilityChange = (entries) => {
  entries.forEach((entry) => {
    if (entry.intersectionRatio > 0 && entry.isIntersecting) {
      entry.target.dispatchEvent(new CustomEvent('mapVisible'));
    } else {
      entry.target.dispatchEvent(new CustomEvent('mapHide'));
    }
  });
};

/**
 * Creating an observer to detect when the map is visible or not.
 */
const mapObserver = (container) => {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
  };
  const observer = new IntersectionObserver(onMapVisibilityChange, options);
  observer.observe(container);
};

/**
 * If the Google Maps instance is not loading, load it.
 * If it is loading, wait for it to load.
 * If it is loaded, resolve the promise
 * @param googleMapsLoaded - A function that is called when the Google Maps instance has loaded.
 * @param googleMapsErrored - A function that will be called if the Google Maps script fails to load.
 * @returns A promise.
 */
export default function loadGoogleMaps(googleMapsLoaded, googleMapsErrored) {
  if (!mapInstance.isGoogleMapsLoading) {
    // Set the Google Maps instance as loading to prevent multiple requests.
    mapInstance.isGoogleMapsLoading = true;
    return new Promise(appendGoogleMapsScript).then(googleMapsLoaded).catch(googleMapsErrored);
  }

  if (mapInstance.isGoogleMapLoaded) {
    // If the Google Maps instance is already loaded resolve the promise.
    return Promise.resolve().then(googleMapsLoaded).catch(googleMapsErrored);
  }

  // Otherwise wait to review if the Google Maps instance has loaded.
  return waitForGoogleMaps().then(googleMapsLoaded).catch(googleMapsErrored);
}

/**
 * Create a new map instance and add a listener to the map's tilesloaded event.
 *
 * @param container - The DOM element that will contain the map.
 * @param settings - The settings object that you would normally pass to the Google Maps constructor.
 * @param events - An object with the functions that will be added to the map events is loaded.
 */
export function createMapInstance(container, settings, events) {
  settings.mapId = settings.mapId || mapInstance.mapId;
  mapInstance.map = new window.google.maps.Map(container, settings);

  window.google.maps.event.addListenerOnce(mapInstance.map, 'tilesloaded', () => {
    const mapInitialInformation = {
      zoom: mapInstance.map.getZoom(),
    };

    events.mapLoaded(mapInitialInformation);
  });

  window.google.maps.event.addListener(mapInstance.map, 'idle', function () {
    const bounds = this.getBounds();
    events.coordinatesChanged(bounds);
  });

  window.google.maps.event.addListener(mapInstance.map, 'dragstart', () => {
    container.dispatchEvent(new CustomEvent('mapDragStart'));
  });

  window.google.maps.event.addListener(mapInstance.map, 'dragend', () => {
    container.dispatchEvent(new CustomEvent('mapDragEnd'));
  });

  window.google.maps.event.addListener(mapInstance.map, 'click', (event) => {
    container.dispatchEvent(
      new CustomEvent('mapClick', {
        detail: {
          latLng: event.latLng,
        },
      }),
    );
  });

  let currentZoom = mapInstance.map.getZoom();
  window.google.maps.event.addListener(mapInstance.map, 'zoom_changed', () => {
    const newZoom = mapInstance.map.getZoom();
    container.dispatchEvent(
      new CustomEvent('zoomChanged', {
        detail: {
          zoomLevel: newZoom,
          isZoomIn: newZoom > currentZoom,
        },
      }),
    );
    currentZoom = newZoom;
  });

  mapObserver(container);
}

/**
 * removeMarkers: Clear the markers from the map and from the mapInstance memory.
 * @public
 */
export function removeMarkers() {
  mapInstance.markers.forEach((item) =>
    setTimeout(() => {
      item.map = null;
    }, 0),
  );
  mapInstance.markers = [];
  mapInstance.markersHashed = new Map();
}

/**
 * paintMarker: It takes an array of markers configuration, and adds them from the map.
 * @param {object} markersToAdd: array of marker config object.
 * @public
 */
export function paintMarker(markers) {
  const markersToCreate = markers.filter(
    (item) =>
      !mapInstance.markers.some(
        (marker) => marker.position.lat === item.markerPosition.lat && marker.position.lng === item.markerPosition.lng,
      ),
  );

  markersToCreate.forEach((item) => setTimeout(() => createMarker(item), 0));
}

/**
 * updateBoundaries: this function creates a logical squared area that grows depending on the markers plotted in a map.
 *  In order to create that area, we rely in North, South, East and West boundaries received as parameter. These 4
 *  points represent the MinLatitude, MinLongitude, MaxLatitude and MaxLongitude new boundaries to set on the map.
 * @param {Array} mapCorners: Object containing a MinLatitude, MinLongitude, MaxLatitude and MaxLongitude. These
 * coordinates will determine the boundaries of the map.
 * @public
 */
export function updateBoundaries(mapBoundaries) {
  const properties = ['MinLatitude', 'MinLongitude', 'MaxLatitude', 'MaxLongitude'];
  const isValidBoundary = properties.every((property) => Object.prototype.hasOwnProperty.call(mapBoundaries, property));

  if (!isValidBoundary) {
    return;
  }

  /*
    A LatLngBounds is a rectangular area defined by two points (the southwest and northeast corners)
  */
  const bounds = new google.maps.LatLngBounds();
  const southwestPoint = new google.maps.LatLng(mapBoundaries.MinLatitude, mapBoundaries.MinLongitude);

  bounds.extend(southwestPoint);

  const northeastPoint = new google.maps.LatLng(mapBoundaries.MaxLatitude, mapBoundaries.MaxLongitude);

  bounds.extend(northeastPoint);

  mapInstance.map.fitBounds(bounds);
}

/**
 * updateMarkers: From the instance array of markers, adjust their visibility from the map.
 */
export function updateMarkersVisibilityDeprecated() {
  const bounds = mapInstance.map.getBounds();
  const zoom = mapInstance.map.getZoom();

  if (zoom < 12) {
    setTimeout(() => {
      mapInstance.markersHashed.forEach((markers) => {
        const sorted = markers.sort((a, b) => b.zIndex - a.zIndex);
        setTimeout(() => setMarkerVisibility(sorted[0], bounds, zoom), 0);

        sorted.slice(1).forEach((marker) => {
          if (marker.map) {
            setTimeout(() => marker.setMap(null), 0);
          }
        });
      });
    }, 0);
  } else {
    setTimeout(
      () => mapInstance.markers.forEach((item) => setTimeout(() => setMarkerVisibility(item, bounds, zoom), 0)),
      0,
    );
  }
}

/**
 * updateMapCenter: It takes an object with coordinates, and updates the map center.
 * @param coordinates - {latitude: number, longitude: number}
 */
export function updateMapCenter(coordinates) {
  mapInstance.map.setCenter({ lat: coordinates.latitude, lng: coordinates.longitude });
}

/**
 * Gets the current zoom level of the map instance.
 * @returns {number} The current zoom level of the map.
 */
export function getMapZoom() {
  return mapInstance?.map.getZoom() ?? 0;
}

/**
 * Updates the visibility of marker groups on the map based on their priority.
 *
 * For each group of markers:
 * - Sorts markers by their zIndex in descending order.
 * - If the highest zIndex in the group is less than or equal to the priority threshold,
 *   all markers in the group are removed from the map.
 * - Otherwise, only the top marker is made visible (if within bounds and zoom), and the rest are removed.
 *
 * @param {number} [priorityThreshold=0] - The minimum zIndex required for a marker group to be visible.
 */
export function updateMarkerGroupsByPriority(priorityThreshold = 0) {
  const bounds = mapInstance.map.getBounds();
  const zoom = mapInstance.map.getZoom();

  setTimeout(() => {
    mapInstance.markersHashed.forEach((markers) => {
      if (markers.length === 0) {
        return;
      }
      const sorted = [...markers].sort((a, b) => b.zIndex - a.zIndex);
      const topMarker = sorted[0];

      if (topMarker.zIndex <= priorityThreshold) {
        sorted.forEach((marker) => {
          if (marker.map) {
            setTimeout(() => marker.setMap(null), 0);
          }
        });
        return;
      }

      setTimeout(() => setMarkerVisibility(sorted[0], bounds, zoom), 0);

      sorted.slice(1).forEach((marker) => {
        if (marker.map) {
          setTimeout(() => marker.setMap(null), 0);
        }
      });
    });
  }, 0);
}

/**
 * Updates the visibility of all markers on the map based on current map bounds and zoom level.
 * Iterates through all markers in the map instance and asynchronously determines their visibility.
 * Uses setTimeout to defer visibility updates to prevent blocking the main thread.
 * @function updateMarkersVisibility
 * @returns {void}
 */
export function updateMarkersVisibility() {
  const bounds = mapInstance.map.getBounds();
  const zoom = mapInstance.map.getZoom();
  setTimeout(
    () =>
      mapInstance.markers.forEach((marker) => {
        setTimeout(() => setMarkerVisibility(marker, bounds, zoom), 0);
      }),
    0,
  );
}

/**
 * showMarker function will find an Advanced Marker and execute the function setMarkerVisibility on it.
 * @param {int} communityId is the identifier of the marker we want to show.
 */
export function showMarkers(communityId) {
  const markersToShow = mapInstance.markers.filter((item) => item.communityId === communityId);
  const bounds = mapInstance.map.getBounds();
  const zoom = mapInstance.map.getZoom();

  markersToShow.forEach((markerToShow) => {
    markerToShow.ignoreVisibilityUpdates = false;
    setMarkerVisibility(markerToShow, bounds, zoom);
  });
}

/**
 * hideMarker function will find an Advanced Marker and set it's map property to null.
 * @param {int} communityId is the identifier of the marker we want to hide.
 */
export function hideMarkers(communityId) {
  const markersToHide = mapInstance.markers.filter((item) => item.communityId === communityId);

  markersToHide.forEach((markerToHide) => {
    markerToHide.ignoreVisibilityUpdates = true;
    markerToHide.setMap(null);
  });
}

/**
 * Updats the minimum zoom level of the map. If the current zoom level is less than the new zoom limit,
 * then update the map zoom level to the new zoom limit.
 *
 * In this context minimum zoom level refers to the zoom out level that the map can be set to.
 * The lower the zoom level the more zoomed out the map is.
 *
 * @param newZoomLimit - The new minimum zoom level that the map should be set to.
 * @returns A boolean value indicating if the map current zoom level was updated.
 */
export function updateMinimumZoomLevel(newZoomLimit) {
  const currentZoom = mapInstance.map.getZoom();
  const currentMinZoom = mapInstance.map.minZoom;
  const doesZoomLevelNeedToBeUpdated = currentZoom < newZoomLimit;

  if (currentMinZoom !== newZoomLimit) {
    mapInstance.map.setOptions({ minZoom: newZoomLimit });
  }

  // Google Maps the zoom level lower values indicate a higher zoomed out level.
  if (doesZoomLevelNeedToBeUpdated) {
    mapInstance.map.setZoom(newZoomLimit);
  }

  return doesZoomLevelNeedToBeUpdated;
}

/**
 * dispatchCustomEventOnDocument: dispatches a custom event on the document that holds the map.
 *
 * @param eventName - The event identifier for any subscriber expecting to listen a custom event on the document.
 * @param eventDetail - The event information for the subscriber to a given custom event on the document.
 */
const dispatchCustomEventOnDocument = (eventName, eventDetail) => {
  const event = new CustomEvent(eventName, { detail: eventDetail });
  document.dispatchEvent(event);
};

/**
 * drawBoundary: draws a boundary in the map. The boundary shape should be a KML (Keyhole Markup Language)
 * and it must be requested from an url.
 * For details check: https://developers.google.com/maps/documentation/javascript/kml
 *
 * @param boundaryUrl - The boundary url with the shape to be draw on the map.
 */
export function drawBoundary(boundaryUrl) {
  mapInstance.locationBoundary?.setMap(null);
  const boundaries = {};

  if (!boundaryUrl) {
    dispatchCustomEventOnDocument('newBoundariesShown', { boundaries });
  } else {
    mapInstance.locationBoundary = new window.google.maps.KmlLayer(boundaryUrl, {
      suppressInfoWindows: true,
      preserveViewport: true,
      map: mapInstance.map,
    });

    window.google.maps.event.addListener(mapInstance.locationBoundary, 'defaultviewport_changed', () => {
      boundaries.MaxLatitude = mapInstance.locationBoundary.getDefaultViewport().getNorthEast().lat();
      boundaries.MaxLongitude = mapInstance.locationBoundary.getDefaultViewport().getNorthEast().lng();
      boundaries.MinLatitude = mapInstance.locationBoundary.getDefaultViewport().getSouthWest().lat();
      boundaries.MinLongitude = mapInstance.locationBoundary.getDefaultViewport().getSouthWest().lng();

      dispatchCustomEventOnDocument('newBoundariesShown', { boundaries });
    });

    window.google.maps.event.addListener(mapInstance.locationBoundary, 'status_changed', () => {
      if (mapInstance.locationBoundary.getStatus() !== 'OK') {
        dispatchCustomEventOnDocument('newBoundariesShown', { boundaries });
      }
    });
  }
}

/**
 * addOverlay function is the first step for rendering an OverlayView. It is in charge of adding the container of the
 * OverlayView in the desired map.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn, for instance: north,
 * south, east and west values where the OverlayView will be rendered.
 */
const addOverlay = function (resource) {
  const panes = this.getPanes();
  const mainWrapper = panes.overlayLayer.querySelector(`[data-community-map-wrapper-id="${resource.communityId}"]`);
  panes.overlayMouseTarget.appendChild(resource.overlay);

  if (resource.isInteractiveSitePlan && resource.polygonsContainer) {
    panes.overlayLayer.appendChild(resource.polygonsContainer);
  }

  if (!mainWrapper) {
    panes.mapPane.appendChild(resource.container.parentElement);
  }
};

/**
 * isOverlayInViewport will return true if the overlay is within map vieport, false if not.
 * @param {Object} sitePlanId to check if is inside of the viewport.
 */
export function isSitePlanInViewport(sitePlanId) {
  const overlay = mapInstance.sitePlans.find((sitePlan) => sitePlan.sitePlanId === sitePlanId);

  if (!overlay) {
    return false;
  }

  const mapBounds = mapInstance.map.getBounds();
  return mapBounds.intersects(overlay.overlayBounds);
}

/**
 * isSitePlanIntersectingMapBounds function will check if a given siteplan is intersecting the map viewport.
 * @param {Object} sitePlan to identify if it is intersecting user viewport or not.
 * @returns a flag indicating whether a siteplan is intersecting map viewport or not.
 */
export function isSitePlanIntersectingMapBounds(sitePlan) {
  const sitePlanBounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(sitePlan.south, sitePlan.west),
    new google.maps.LatLng(sitePlan.north, sitePlan.east),
  );

  const mapBounds = mapInstance.map.getBounds();
  return mapBounds.intersects(sitePlanBounds);
}

/**
 * drawOverlay function will be called every time the map is dragged around. It is in charge of moving the OverlayView
 * along with the map so it keeps fitting in the provided creation coordinates.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn, for instance: north,
 * south, east and west values where the OverlayView will be rendered.
 */
const drawOverlay = function (resource) {
  const overlayProjection = this.getProjection();

  const overlayBounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(resource.south, resource.west),
    new google.maps.LatLng(resource.north, resource.east),
  );

  const southWestPoint = overlayProjection.fromLatLngToDivPixel(overlayBounds.getSouthWest());
  const northEastPoint = overlayProjection.fromLatLngToDivPixel(overlayBounds.getNorthEast());

  this.overlayBounds = overlayBounds;

  resource.onDraw?.(resource, southWestPoint, northEastPoint);
};

/**
 * removeOverlay function will remove an overlay from the map.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn, for instance: north,
 * south, east and west values where the OverlayView will be rendered.
 */
const removeOverlay = function (resource) {
  resource.onRemove?.(resource);
};

/**
 * createSitePlanOverlay is the way map library facade exposes the OverlayView of Google Maps. By calling
 * this function, the interested caller will get an OverlayView rendered in the Search Results' map.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn, for instance: north,
 * south, east and west values where the OverlayView will be rendered.
 */
export function createSitePlanOverlay(resource) {
  const overlay = mapInstance.sitePlans.find((plan) => plan.sitePlanId === resource.sitePlanId);

  if (overlay) {
    return;
  }

  const overlayView = new google.maps.OverlayView();
  overlayView.communityId = resource.communityId;
  overlayView.sitePlanId = resource.sitePlanId;
  overlayView.communityMapUrl = resource.imageUrl;
  mapInstance.sitePlans.push(overlayView);

  overlayView.onAdd = addOverlay.bind(overlayView, resource);
  overlayView.draw = drawOverlay.bind(overlayView, resource);
  overlayView.onRemove = removeOverlay.bind(overlayView, resource);

  overlayView.setMap(mapInstance.map);
}

/**
 * removeSitePlanOverlay removes a given Community Map by setting its OverlayView's map to null.
 * The removeOverlay function of the map will be invoked as a result of setting the map property to null,
 * removing any OverlayView related HTML from the DOM.
 * @param {int} communityId is the community map identifier.
 */
export function removeSitePlanOverlay(communityId) {
  const sitePlans = mapInstance.sitePlans.filter((map) => map.communityId === communityId);

  sitePlans.forEach((map) => {
    map.setMap(null);
  });

  mapInstance.sitePlans = mapInstance.sitePlans.filter((map) => map.communityId !== communityId);
}

/**
 * getSitePlansOutOfViewport function will identify and return those community maps
 * that are no longer contained within the user's viewport.
 * @returns a collection of community maps no longer contained in user's viewport.
 */
export function getSitePlansOutOfViewport() {
  const mapBounds = mapInstance.map.getBounds();

  const sitePlansOutOfViewport = mapInstance.sitePlans
    .filter((sitePlan) => {
      try {
        const overlayBounds = sitePlan.overlayBounds;
        const isOverlayVisible = mapBounds.intersects(overlayBounds);

        return !isOverlayVisible;
      } catch (error) {
        return false;
      }
    })
    .map((sitePlan) => ({
      communityId: sitePlan.communityId,
      url: sitePlan.communityMapUrl,
    }));

  return sitePlansOutOfViewport;
}

/**
 * toggleDoubleClickZoom function will toggle the capability of
 * zoomin in when double clicking or double tapping.
 * @param {boolean} isDoubleClickZoomEnabled flag indicating whether
 * double click zoom has to be disabled or not.
 */
export function toggleDoubleClickZoom(isDoubleClickZoomEnabled) {
  mapInstance.map.setOptions({
    disableDoubleClickZoom: isDoubleClickZoomEnabled,
  });
}

/**
 * addOverlayMarkers function will add the container for interactive
 * markers into the overlay mouse target pane of the map.
 * @param {Object} resource with the basic information of the desired OverlayView to be add,
 * for instance: markers to represent, community identifier where those markers belong, the
 * container where the overlay will be shown.
 */
const addOverlayMarkers = function (resource) {
  const panes = this.getPanes();
  panes.markerLayer.appendChild(resource.container);

  resource.onAdd?.(resource);
};

/**
 * drawOverlayMarkers function will be called every time the markers overlay changed zoon level
 * or was dragged around in the map.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn,
 * for instance: markers to represent, community identifier where those markers belong, the
 * container where the overlay will be shown.
 */
const drawOverlayMarkers = function (resource) {
  const overlayProjection = this.getProjection();
  resource.scale = mapInstance.map.getZoom();
  const mapBounds = mapInstance.map.getBounds();

  resource.markers.forEach((marker) => {
    const position = {
      latitude: parseFloat(marker.Latitude),
      longitude: parseFloat(marker.Longitude),
    };

    const center = new google.maps.LatLng(position.latitude, position.longitude);
    marker.center = overlayProjection.fromLatLngToDivPixel(center);
    marker.isVisible = mapBounds.contains(center);
  });

  resource.onDraw?.(resource);
};

/**
 * createInteractiveSitePlanMarkersOverlay function will create an overlay dedicated
 * to the interactive markers on top of a site plan.
 * @param {Object} resource with the basic information of the desired OverlayView to be drawn, for instance:
 * markers to represent, community identifier where those markers belong, the container where the overlay will be shown.
 */
export function createInteractiveSitePlanMarkersOverlay(resource) {
  const overlay = mapInstance.interactiveMarkersOverlays.find((markers) => markers.sitePlanId === resource.sitePlanId);

  if (overlay) {
    return;
  }

  const overlayView = new google.maps.OverlayView();
  overlayView.communityId = resource.communityId;
  mapInstance.interactiveMarkersOverlays.push(overlayView);

  overlayView.onAdd = addOverlayMarkers.bind(overlayView, resource);
  overlayView.draw = drawOverlayMarkers.bind(overlayView, resource);
  overlayView.onRemove = removeOverlay.bind(overlayView, resource);

  overlayView.setMap(mapInstance.map);
}

/**
 * removeInteractiveMarkersOverlay removes a set of interactive markers by setting its OverlayView's map to null.
 * The removeOverlay function of the interactive markers will be invoked as a result of setting the map property
 * to null, removing any OverlayView related HTML from the DOM.
 * @param {int} id is the site plan identifier.
 */
export function removeInteractiveMarkersOverlay(id) {
  const interactiveOverlays = mapInstance.interactiveMarkersOverlays.filter((map) => map.communityId === id);

  interactiveOverlays.forEach((overlay) => overlay.setMap(null));

  mapInstance.interactiveMarkersOverlays = mapInstance.interactiveMarkersOverlays.filter(
    (map) => map.communityId !== id,
  );
}

/**
 * setZoomLevel: will set the map zoom level.
 * @param {int} zoomLevel to set.
 */
export function setZoomLevel(zoomLevel) {
  if (zoomLevel) {
    mapInstance.map.setZoom(zoomLevel);
  }
}

/**
 * changeMapCursorPointer function adjusts the cursor pointer option on the map
 * @param {string} cursorOption to be used on the map cursor.
 */
export function changeMapCursorPointer(cursorOption) {
  const cursor = mapCursor[cursorOption] || mapCursor.default;
  mapInstance.map.setOptions({ draggableCursor: cursor });
}

/**
 * Creates and displays a hovered marker on the map for the given marker configuration.
 *
 * - Instantiates a new AdvancedMarkerElement using the provided marker's icon, position, zIndex, and description.
 * - Sets the marker's DOM element attribute to indicate it is a marker element.
 * - Adds the marker to the map and stores a reference to it in mapInstance.hoveredMarker.
 *
 * @param {Object} marker - The marker configuration object containing icon, markerPosition, zIndex, and description.
 */
export const createHoveredMarker = (marker) => {
  if (!mapInstance.map) {
    return;
  }

  const content = createMarkerContentElement(marker.icon);
  const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
    content,
    position: marker.markerPosition,
    zIndex: marker.zIndex,
    title: marker.description,
    gmpClickable: true,
  });

  advancedMarker.element.dataset.markerElement = 'true';
  advancedMarker.setMap(mapInstance.map);
  mapInstance.hoveredMarker = advancedMarker;
};

/**
 * Updates the content of an existing marker on the map.
 *
 * - Finds the marker in the mapInstance.markers array by its id.
 * - If found, replaces its content with the new HTML content provided.
 * - The new content is converted from an HTML string to a DOM element using createMarkerContentElement.
 *
 * @param {number|string} id - The unique identifier of the marker to update.
 * @param {string} newContent - The new HTML string to set as the marker's content.
 */
export const updateMarkerContent = (id, newContent) => {
  const content = createMarkerContentElement(newContent);
  const markerToUpdate = mapInstance.markers.find((marker) => +marker.id === id);
  if (markerToUpdate) {
    markerToUpdate.content = content;
  }
};

/**
 * Removes the currently displayed hovered marker from the map, if one exists.
 *
 * - Checks if a hovered marker is present in the mapInstance.
 * - If present, removes it from the map and clears the reference.
 */
export const removeHoveredMarker = () => {
  if (!mapInstance.hoveredMarker) {
    return;
  }

  mapInstance.hoveredMarker.setMap(null);
  mapInstance.hoveredMarker = null;
};

/**
 * Retrieves the current bounds of the map instance.
 *
 * @returns {{north: number, east: number, south: number, west: number} | null}
 * An object containing the geographical bounds with north, east, south, and west coordinates,
 * or null if bounds are not available.
 *
 * @example
 * const bounds = getMapBounds();
 * if (bounds) {
 *   console.log(`North: ${bounds.north}, East: ${bounds.east}`);
 * }
 */
export const getMapBounds = () => {
  const bounds = mapInstance.map.getBounds();
  if (!bounds) {
    return null;
  }

  const north = bounds.getNorthEast().lat();
  const east = bounds.getNorthEast().lng();
  const south = bounds.getSouthWest().lat();
  const west = bounds.getSouthWest().lng();

  return {
    north,
    east,
    south,
    west,
  };
};

/**
 * Checks if the Map pin lat and long are within the current viewport.
 * @param {number} latitude - The latitude of the pin to check.
 * @param {number} longitude - The longitude of the pin to check.
 *
 * @returns true if the pin is within the viewport, false otherwise.
 *
 * @example
 * const isPinOnViewport = isMapPinOnViewport(51.5074, -0.1278);
 * if (isPinOnViewport) {
 *   console.log('The pin is within the viewport.');
 * }
 */
export const isMapPinOnViewport = (latitude, longitude) => {
  if (!mapInstance.map) {
    return false;
  }

  const bounds = mapInstance.map.getBounds();
  const position = new google.maps.LatLng(latitude, longitude);

  if (!bounds) {
    return false;
  }
  return bounds.contains(position);
};
